import { Box, Center, Spinner, Text } from '@chakra-ui/core';
import React from 'react';
import { Column, useSortBy, useTable } from 'react-table';
import { Table, Tbody, Td, Tfoot, Tr } from '@chakra-ui/table';
import { RecruiterTableRow } from './RecruiterTableRow';
import { Position, Recruiter } from '../types';
import { TablePaginationFooter } from '../../../../../app/components/TablePaginationFooter';
import { useStoreState } from '../../../../../models/hooks';

interface RecruiterTableProps {
  recruiters: Recruiter[];
  allRecruitersCount: number;
  position: Position;
  isLoading: boolean;
}

export const RecruiterTable: React.FC<RecruiterTableProps> = ({ recruiters, position, isLoading }) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(20);
  const selectedRecruiters = useStoreState((state) => state.moniterByRecruiters.selectedRecruiters);

  const paginatedRecruiters = React.useMemo(() => {
    const offset = (currentPage - 1) * itemsPerPage;
    return recruiters.slice(offset, offset + itemsPerPage);
  }, [recruiters, currentPage, itemsPerPage]);

  const columns: Column<Recruiter>[] = React.useMemo(
    () => [
      {
        Header: 'Recruiters',
        accessor: 'id', // Using 'id' as a stable accessor
        id: 'recruiterName', // Keeping id for rendering logic in row component
        width: '60%',
      },
      {
        Header: '',
        accessor: 'id',
        id: 'action',
        width: '40%',
        disableSortBy: true,
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, rows, prepareRow } = useTable(
    {
      data: paginatedRecruiters,
      columns,
    },
    useSortBy,
  );

  if (isLoading) {
    return (
      <Center py={5} data-testid="recruiter-table-loading">
        <Spinner size="md" colorScheme="blue" />
      </Center>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        maxHeight: 'calc(100vh - 440px)',
        overflow: 'auto',
      }}
    >
      <Table {...getTableProps()} variant="simple" sx={{ width: '100%', height: '100%', minHeight: 0 }}>
        <Tbody
          {...getTableBodyProps()}
          sx={{
            padding: '8px',
          }}
        >
          {rows.length > 0 ? (
            rows.map((row) => {
              prepareRow(row);
              const isSelected = selectedRecruiters?.includes(row.original.id);
              return <RecruiterTableRow key={row.original.id} row={row} position={position} isSelected={isSelected} />;
            })
          ) : (
            <Tr>
              <Td colSpan={columns.length}>
                <Center py={5}>
                  <Text>No recruiters to display for the selected filter.</Text>
                </Center>
              </Td>
            </Tr>
          )}
        </Tbody>
        <Tfoot position="sticky" bottom={0} bg="white" zIndex={1}>
          <Tr>
            <Td colSpan={columns.length}>
              <TablePaginationFooter
                finalListOfItems={recruiters}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                setCurrentItemsList={/* istanbul ignore next */ () => {}}
                sx={{
                  padding: 2,
                  borderTop: '1px solid #e5e5e5',
                }}
                paginationTextStyle={{
                  fontSize: 'sm',
                  color: 'gray.600',
                }}
              />
            </Td>
          </Tr>
        </Tfoot>
      </Table>
    </Box>
  );
};
